import { DialogController, DialogService } from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { SendCertificatesChoiceDialog } from 'components/dialogs/send-certificates-choice/send-certificates-choice-dialog';
import { PubSub } from 'lib/event/PubSub';
import { categoryDimensionTypes, SendMooringSalesOrderCertificateToMe, SendMooringSalesOrderCertificateToCustomer } from 'models/mooring';

import { AquacomSyncStatus, CreateUpdateMooringSalesOrder, MooringCertificate, MooringSalesOrder } from 'models/mooring';

import { ErrorService } from 'services';
import { MooringSalesOrderService } from 'services/mooring-sales-order-service';
import { SystemFileStorageService } from 'services/system-file-storage-service';
import { ToastService } from 'services/toast-service';
import './salesorder-dialog.scss';
import { SiteService } from 'services/site-service';
import { CustomerService } from 'services/customer-service';
import { CustomerStatus } from 'models/Customer';
import { connectTo } from 'aurelia-store';
import { ApplicationState } from 'lib/state';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AsyncErrorHandler } from 'lib/ui';

@autoinject
@connectTo()
export class SalesOrderDialog {
  protected salesOrder?: MooringSalesOrder;

  protected ready: boolean = false;
  protected form?: CreateUpdateMooringSalesOrder;

  protected isSaving = false;
  protected isLocked = false;
  protected isTransfering = false;

  protected inclueAllCertificates = false;

  protected isStandard = true;

  protected certificate: MooringCertificate = new MooringCertificate();

  protected canTransferToAquacom = false;
  protected canUpdateAquacomCertificates = false;

  protected exportProgress = false;
  protected saveProgress = false;
  protected sendCertificatesToMeProgress = false;
  protected sendCertificatesToCustomerProgress = false;

  protected isEmailValid = false;

  private state: ApplicationState;

  protected activeFrameCertificates: {
    BatchNumber: string;
    BatchNumberTo: string;
    AquacomCertificateId: number;
  }[] = [];

  constructor(
    private dialogController: DialogController,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private salesOrderService: MooringSalesOrderService,
    private toastService: ToastService,
    private systemFileService: SystemFileStorageService,
    private t: I18N,
    private confirmation: ConfirmDialog,
    private pubSub: PubSub,
    private customerService: CustomerService,
    private eventAggregator: EventAggregator
  ) {}

  get zipFileText() {
    return `${this.t.tr('general.download')} zip`;
  }

  get approvalText() {
    if (this.isLocked) {
      return `${this.t.tr('general.order')} ${this.t.tr('general.approved').toLowerCase()}`;
    }
    return `${this.t.tr('general.approve')} ${this.t.tr('general.order').toLowerCase()}`;
  }

  get unlockText() {
    return `${this.t.tr('general.unlockSalesOrder')}`;
  }

  get sendCertificateToMeText() {
    return `${this.t.tr('general.sendCertificatesToMe')}`;
  }

  get sendText() {
    return `${this.t.tr('general.sendCertificates')}`;
  }

  protected async approveOrder() {
    try {
      this.saveProgress = true;

      if (this.salesOrder.FrameOrderLines.length > 0) {
        const canApprove = await this.checkCanApprove();
        if (!canApprove) {
          this.toastService.showWarning('mooring.canNotApproveFrameWarning');
          return;
        }
      }

      let emptyLines = 0;
      this.salesOrder.FrameOrderLines.forEach((orderLine) => {
        if (orderLine.CertificateId === null) {
          emptyLines++;
        }
      });

      const approved = await this.confirmation.confirmYesCancel(
        'general.approve',
        emptyLines > 0 ? 'mooring.confirmApprovalOfSalesOrderWithEmptyLines' : 'mooring.confirmApprovalOfSalesOrder'
      );
      if (approved) {
        await this.salesOrderService.approve(this.salesOrder.Id, this.inclueAllCertificates);
        await this.getSalesOrder(this.salesOrder.Id);
      }
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.saveProgress = false;
    }
  }

  protected async sendCertificateToMe() {
    try {
      const dialog = this.dialogService.open({ viewModel: SendCertificatesChoiceDialog });
      const sendMode = await dialog.whenClosed();

      if (sendMode.wasCancelled) {
        return;
      }

      this.sendCertificatesToMeProgress = true;
      const sendMooringSalesOrderCertificates: SendMooringSalesOrderCertificateToMe = new SendMooringSalesOrderCertificateToMe();
      sendMooringSalesOrderCertificates.Id = this.form.Id;
      sendMooringSalesOrderCertificates.OrderNumber = this.form.OrderNumber;
      sendMooringSalesOrderCertificates.SenderName = this.state.user.Name;
      sendMooringSalesOrderCertificates.RecipientEmail = this.state.user.Email;
      sendMooringSalesOrderCertificates.LocalityId = this.form.SiteId;
      sendMooringSalesOrderCertificates.CertificatesZipId = this.salesOrder.CertificatesZipId;
      sendMooringSalesOrderCertificates.SendType = sendMode.output;
      await this.salesOrderService.sendCertificatesToMe(sendMooringSalesOrderCertificates);
      await this.getSalesOrder(this.salesOrder.Id);
      this.sendCertificatesToMeProgress = false;
      this.toastService.showSuccess('general.certificatesSent');
    } catch (error) {
      this.errorService.handleError(error);
      this.sendCertificatesToMeProgress = false;
    }
  }

  protected async sendCertificateToCustomer() {
    if (!this.form.SendToEmailAddress) {
      return this.toastService.showError('general.requiredEmailField');
    }

    try{
      const dialog = this.dialogService.open({ viewModel: SendCertificatesChoiceDialog });
      const sendMode = await dialog.whenClosed();

      if (sendMode.wasCancelled) {
        return;
      }

      this.sendCertificatesToCustomerProgress = true;
      const sendMooringSalesOrderCertificateToCustomerObj: SendMooringSalesOrderCertificateToCustomer =
        new SendMooringSalesOrderCertificateToCustomer();
      sendMooringSalesOrderCertificateToCustomerObj.Id = this.form.Id;
      sendMooringSalesOrderCertificateToCustomerObj.OrderNumber = this.form.OrderNumber;
      sendMooringSalesOrderCertificateToCustomerObj.SenderEmail = this.state.user.Email;
      sendMooringSalesOrderCertificateToCustomerObj.SenderName = this.state.user.Name;
      sendMooringSalesOrderCertificateToCustomerObj.RecipientEmail = this.form.SendToEmailAddress;
      sendMooringSalesOrderCertificateToCustomerObj.LocalityId = this.form.SiteId;
      sendMooringSalesOrderCertificateToCustomerObj.CertificatesZipId = this.salesOrder.CertificatesZipId;
      sendMooringSalesOrderCertificateToCustomerObj.SendType = sendMode.output;
      await this.salesOrderService.sendCertificatesToCustomer(sendMooringSalesOrderCertificateToCustomerObj);
      await this.getSalesOrder(this.salesOrder.Id);
      this.sendCertificatesToCustomerProgress = false;
      this.toastService.showSuccess('general.certificatesSent');
    } catch (error) {
      this.errorService.handleError(error);
      this.sendCertificatesToCustomerProgress = false;
    }
  }

  protected async downloadFile(fileId: number) {
    try {
      this.exportProgress = true;
      await this.systemFileService.download(fileId, true);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.exportProgress = false;
    }
  }

  // Aurelia lifecycle
  protected async activate(model: { salesOrderId: number }) {
    if (model?.salesOrderId) {
      await this.setup(model.salesOrderId);
    }

    this.eventAggregator.subscribe('validateEmail', (isEmailValid: boolean) => {
      this.isEmailValid = isEmailValid;
    });

    this.ready = true;
  }

  protected async transferToAquacom() {
    try {
      this.isTransfering = true;
      await this.salesOrderService.transferToAquacom(this.salesOrder.Id);
      await this.getSalesOrder(this.salesOrder.Id);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isTransfering = false;
    }
  }

  @AsyncErrorHandler
  protected async getSalesOrder(id: number) {
    this.salesOrder = await this.salesOrderService.get(id);
    const filteredFramedOrderLines = this.salesOrder.FrameOrderLines.filter((x) => x.FrameOrderCategoryId != 1);
    this.salesOrder.FrameOrderLines = filteredFramedOrderLines;

    if (this.salesOrder.FrameOrderLines.length > 0) {
      this.isStandard = false;

      this.activeFrameCertificates = (this.salesOrder?.FrameOrderLines || [])
        .filter((line) => line.IsAquacomCertificateActive && line.AquacomComponentCertificateId)
        .map((line) => ({
          AquacomCertificateId: line.AquacomComponentCertificateId,
          BatchNumber: line.BatchNumber,
          BatchNumberTo: line.BatchNumberTo || '',
        }));
    }

    if (this.salesOrder.ApprovedAt) {
      this.isLocked = true;
    } else {
      this.isLocked = false;
    }

    this.canTransferToAquacom = this.salesOrder.AquacomSyncStatus !== AquacomSyncStatus.SYNCED && Boolean(this.salesOrder.ApprovedAt);

    this.canUpdateAquacomCertificates = this.salesOrder.CanSyncToAquacom && this.salesOrder.AquacomSyncStatus === AquacomSyncStatus.SYNCED;
  }

  protected unlockProgress = false;
  protected async unlockSalesOrder() {
    try {
      const approve = await this.confirmation.confirmYesCancel('general.unlock', 'mooring.confirmUnlockOfSalesOrder');
      if (approve) {
        this.unlockProgress = true;
        await this.salesOrderService.unlock(this.salesOrder.Id);
        await this.getSalesOrder(this.salesOrder.Id);
      }
    } catch (error) {
      this.errorService.handleError(error);
    }
    this.unlockProgress = false;
  }

  protected async setup(salesOrderId?: number) {
    if (salesOrderId) {
      await this.getSalesOrder(salesOrderId);
      let lastId = -1;
      this.form = {
        Id: this.salesOrder.Id,
        OrderNumber: this.salesOrder.OrderNumber,
        SiteId: this.salesOrder.SiteId,
        TypeId: this.salesOrder.TypeId,
        Note: this.salesOrder.Note,
        DeliveryDate: this.salesOrder.DeliveryDate,
        SentFromId: this.salesOrder.SentFromId,
        SendToEmailAddress: this.salesOrder.SendToEmailAddress,
        SentToEmailAddressAt: this.salesOrder.SentToEmailAddressAt,
        Locale: this.salesOrder.Locale,

        OrderLines: this.salesOrder?.OrderLines?.map((x) => {
          //Add dimension
          const primaryDimensionTypeId = x.PrimaryDimensionType;
          const secondaryDimensionTypeId = x.SecondaryDimensionType;
          const primaryDimensionType = categoryDimensionTypes.find((x) => x.value == primaryDimensionTypeId);
          const secondaryDimensionType = categoryDimensionTypes.find((x) => x.value == secondaryDimensionTypeId);

          let dimension = '';
          if (primaryDimensionType) {
            dimension += `${x.PrimaryDimension} ${this.t.tr(primaryDimensionType?.labelShort)}`;
          }
          if (secondaryDimensionType) {
            dimension += ` / ${x.SecondaryDimension} ${this.t.tr(secondaryDimensionType?.labelShort)}`;
          }
          return {
            Id: x.Id,
            Quantity: x.Quantity,
            Category: x.Category,
            Type: x.Type,
            ProductId: x.ProductId,
            ArticleNumber: x.ArticleNumber,
            Product: x.Product,

            PrimaryDimension: x.PrimaryDimension,
            PrimaryDimensionType: x.PrimaryDimensionType,
            SecondaryDimension: x.SecondaryDimension,
            SecondaryDimensionType: x.SecondaryDimensionType,
            Dimension: dimension,
            SalesOrderId: x.SalesOrderId,
            ActualBatchNumber: x.ActualBatchNumber,
            CertificateId: x.CertificateId,
            UnitOfMeasure: x.UnitOfMeasure,
            ArticleProductId: x.ArticleProductId,
          };
        }),
        // Sorts by category and then by id
        FrameOrderLines: this.salesOrder?.FrameOrderLines.sort((a, b) => {
          if (a.FrameOrderCategorySortIndex !== b.FrameOrderCategorySortIndex) {
            return a.FrameOrderCategorySortIndex - b.FrameOrderCategorySortIndex;
          } else {
            return a.Id - b.Id;
          }
        })?.map((x) => {
          const _isFirst = lastId !== x.FrameOrderCategorySortIndex;
          lastId = x.FrameOrderCategorySortIndex;

          const canEditLength = x.MooringArticleCategoriType === 'kjetting' || x.MooringArticleCategoriType === 'tau' ? true : false;
          let totalLength = 0;
          if (canEditLength) {
            if (x.Length && !x.Quantity) {
              totalLength = x.Length;
            } else {
              totalLength = x.Length * x.Quantity;
            }
          }

          //Add dimension
          const primaryDimensionTypeId = x.PrimaryDimensionType;
          const secondaryDimensionTypeId = x.SecondaryDimensionType;
          const primaryDimensionType = categoryDimensionTypes.find((x) => x.value == primaryDimensionTypeId);
          const secondaryDimensionType = categoryDimensionTypes.find((x) => x.value == secondaryDimensionTypeId);

          let dimension = '';
          if (primaryDimensionType) {
            dimension += `${x.PrimaryDimension} ${this.t.tr(primaryDimensionType?.labelShort)}`;
          }
          if (secondaryDimensionType) {
            dimension += ` / ${x.SecondaryDimension} ${this.t.tr(secondaryDimensionType?.labelShort)}`;
          }

          return {
            Id: x.Id,
            Length: x.Length,
            Quantity: x.Quantity,
            TotalLength: totalLength,
            ProductType: x.ProductType,
            SalesOrderId: x.SalesOrderId,
            CanEditLength: canEditLength,
            TypeId: x.TypeId,
            Type: x.Type,
            CertificateId: x.CertificateId,
            FrameOrderCategoryId: x.FrameOrderCategoryId,
            PrimaryDimension: x.PrimaryDimension,
            PrimaryDimensionType: x.PrimaryDimensionType,
            SecondaryDimension: x.SecondaryDimension,
            SecondaryDimensionType: x.SecondaryDimensionType,
            MooringArticleCategoriType: x.MooringArticleCategoriType,
            Dimension: dimension,
            ActualBatchNumber: x.ActualBatchNumber,
            _certificateActiveInAquacom: x.IsAquacomCertificateActive,
            _typeMBL: x.TypeMBL,
            _isFirst,
          };
        }),
        FrameOrderDetails: {
          Id: this.salesOrder?.FrameOrderDetails?.Id,
          CagesX: this.salesOrder?.FrameOrderDetails?.CagesX,
          CagesY: this.salesOrder?.FrameOrderDetails?.CagesY,
          CageLength: this.salesOrder?.FrameOrderDetails?.CageLength,
          CageWidth: this.salesOrder?.FrameOrderDetails?.CageWidth,
          MountedAt: this.salesOrder?.FrameOrderDetails?.MountedAt,
          ProductName: this.salesOrder?.FrameOrderDetails?.ProductName,
          ForUseIn: this.salesOrder?.FrameOrderDetails?.ForUseIn,
        },
        CustomerId: this.salesOrder.CustomerId,
        ContactId: this.salesOrder.ContactId,
        InvoiceNumber: this.salesOrder.InvoiceNumber,
      };
    }
  }

  protected customerStatus: CustomerStatus;

  protected async getCustomerStatus() {
    try {
      const customerId = this.form.CustomerId;
      if (!customerId) return;
      const { data } = await this.customerService.getCustomerStatus(customerId);
      this.customerStatus = data;
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected async onSave(data: CreateUpdateMooringSalesOrder) {
    this.isSaving = true;
    let id = this.salesOrder?.Id || data?.Id;
    data.Id = id;

    const frameOrderLines = data.FrameOrderLines;
    //Length field have to support both , and . so we have to reformat it before we send it to backend
    if (frameOrderLines?.length) {
      for (const frameOrderLine of frameOrderLines) {
        if (frameOrderLine.Length != null && frameOrderLine.Length.toString().includes(',')) {
          const length = frameOrderLine.Length.toString().replace(',', '.');
          frameOrderLine.Length = parseFloat(length);
        }
      }
    }

    try {
      if (id) {
        await this.salesOrderService.update(data, id);
        this.pubSub.publish('list-entity:created', null);
      } else {
        const response = await this.salesOrderService.create(data);
        id = response.Id;
        this.pubSub.publish('list-entity:updated', null);
      }
      await this.setup(id);
      this.toastService.showSuccess('general.saved');
    } catch (error) {
      this.errorService.handleError(error);
    }
    this.isSaving = false;
  }

  protected onCancel() {
    void this.dialogController.close(true);
  }

  protected async checkCanApprove() {
    const { CanApprove } = await this.salesOrderService.checkCanApprove(this.salesOrder.Id);
    return CanApprove;
  }
}
